import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useApi from '../hooks/useApi';
import { useSeason } from '../contexts/SeasonContext';
import { FIXED_H } from './layoutConstants';
import TextStep from './TextStep';

const MONTHS_FR = ['janv.','févr.','mars','avr.','mai','juin','juil.','août','sept.','oct.','nov.','déc.'];
const fmtDate = (d) => {
  const dt = new Date(d);
  return `${dt.getDate()} ${MONTHS_FR[dt.getMonth()]} ${dt.getFullYear()}`;
};

// Chiffres romains décoratifs (effet "numéro de page journal")
const ROMAN = ['I','II','III','IV','V','VI','VII','VIII','IX','X'];
const roman = (i) => ROMAN[i] ?? String(i + 1);

const CurrentEvents = ({ setCurrentSection, setActiveTab }) => {
  const { data: allEvents } = useApi('events');
  const { season } = useSeason();
  const [activeIndex, setActiveIndex] = useState(0);

  const now = new Date();
  const seasonStart = season ? new Date(season.start_date) : null;
  const seasonEnd   = season ? new Date(season.end_date)   : null;

  const items = allEvents
    .filter(e => {
      if (Number(e.active) !== 1) return false;
      const eStart = new Date(e.start_date);
      const eEnd   = e.end_date ? new Date(e.end_date) : null;
      if (eStart > now) return false;
      if (eEnd && eEnd < now) return false;
      if (seasonStart && eEnd && eEnd < seasonStart) return false;
      if (seasonEnd   && eStart > seasonEnd) return false;
      return true;
    })
    .sort((a, b) => Number(a.display_order) - Number(b.display_order));

  const n = items.length;

  useEffect(() => { setActiveIndex(0); }, [n]);

  if (!n) return null;

  const ev = items[activeIndex] ?? items[0];

  return (
    <section className="relative" style={{ background: 'var(--gradient-brand)' }}>
      <div className="flex flex-col md:flex-row" style={{ '--fh': `${FIXED_H}px` }}>

        {/* ══ Image épinglée — discrète, le texte prime ═══════════ */}
        <div
          className="relative z-20 w-full md:w-2/5 shrink-0 sticky md:self-start overflow-hidden bg-stone-900 h-[26vh] md:h-[calc(100dvh-var(--fh))]"
          style={{ top: FIXED_H }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {ev.image_url
                ? <img src={ev.image_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-gradient-to-br from-brand-900 to-stone-950" />
              }
            </motion.div>
          </AnimatePresence>

          {/* Fondu bord (desktop) */}
          <div className="hidden md:block absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-brand-900/50 to-transparent pointer-events-none z-10" />

          {/* Numéro de page flottant */}
          <div className="hidden md:flex absolute bottom-8 right-8 z-10 flex-col items-end gap-0.5 select-none">
            <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-white/20">Page</span>
            <AnimatePresence mode="wait">
              <motion.span
                key={activeIndex}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.3 }}
                className="text-5xl font-black text-white/12 leading-none tabular-nums"
              >
                {String(activeIndex + 1).padStart(2, '0')}
              </motion.span>
            </AnimatePresence>
          </div>

          {/* Stepper vertical */}
          {n > 1 && (
            <div className="absolute right-6 md:right-8 top-1/2 -translate-y-1/2 z-10 flex flex-col items-center gap-3">
              <span className="text-sm font-bold font-mono tabular-nums text-white">
                {String(activeIndex + 1).padStart(2, '0')}
              </span>
              <div className="flex flex-col items-center gap-2.5">
                {items.map((_, i) => (
                  <div
                    key={i}
                    className="rounded-full transition-all duration-500"
                    style={{
                      width:           i === activeIndex ? '3px' : '2px',
                      height:          i === activeIndex ? '3.5rem' : '0.75rem',
                      backgroundColor: 'white',
                      opacity:         i === activeIndex ? 1 : 0.2,
                    }}
                  />
                ))}
              </div>
              <span className="text-sm font-mono tabular-nums text-white/40">
                {String(n).padStart(2, '0')}
              </span>
            </div>
          )}
        </div>

        {/* ══ Texte — défile normalement ═══════════════════════════ */}
        {/* Pas de overflow-hidden ici : ça casserait le sticky du bandeau sur mobile. */}
        <div className="relative flex-1">
          {/* Filigrane */}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage: 'repeating-linear-gradient(45deg,#fff 0,#fff 1px,transparent 1px,transparent 40px),repeating-linear-gradient(-45deg,#fff 0,#fff 1px,transparent 1px,transparent 40px)',
            }}
          />

          {/* Bandeau typographique journal — fixe en haut de la colonne texte.
              Sur mobile, épinglé juste sous l'image (pas au même niveau qu'elle). */}
          <div
            className="sticky z-30 top-[calc(var(--fh)+26vh)] md:top-[var(--fh)] flex items-center gap-3 px-8 md:px-14 py-6"
            style={{ background: '#7f1d1d' }}
          >
            <div className="flex-1 h-px bg-white/20" />
            <span className="text-[9px] font-bold tracking-[0.3em] uppercase text-white/35 whitespace-nowrap">
              Actualités du club
            </span>
            <div className="flex-1 h-px bg-white/20" />
          </div>

          {items.map((e, i) => (
            <TextStep
              key={e.id}
              index={i}
              isActive={i === activeIndex}
              onActive={setActiveIndex}
              className="relative z-10 px-8 md:px-14"
            >
              <span className="absolute top-0 right-14 md:right-16 font-black leading-none text-white/[0.09] select-none pointer-events-none"
                style={{ fontSize: 'clamp(3rem, 9vw, 7.5rem)' }}>
                {roman(i)}
              </span>

              <div className="space-y-4 md:space-y-6 max-w-2xl">
                <div className="flex flex-wrap items-center gap-2.5">
                  {e.category && (
                    <span className="text-xs font-bold tracking-[0.2em] uppercase text-white/50 border border-white/20 px-2.5 py-1 rounded-sm">
                      {e.category}
                    </span>
                  )}
                  {e.start_date && (
                    <span className="text-xs text-white/30 font-mono">
                      {fmtDate(e.start_date)}
                    </span>
                  )}
                </div>

                <h2 className="text-[1.75rem] font-bold text-white leading-[1.05]">
                  {e.title}
                </h2>

                <div className="flex items-center gap-2">
                  <div className="h-px w-8 bg-white/30" />
                  <div className="h-px w-16 bg-white/12" />
                </div>

                <p className="text-white/70 leading-relaxed text-base md:text-lg">
                  {e.description}
                </p>

                {e.cta_text && e.cta_section && (
                  <div className="pt-1 md:pt-2">
                    {e.cta_section.startsWith('http')
                      ? (
                        <a
                          href={e.cta_section}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn bg-white text-brand-800 text-base font-semibold px-7 py-3.5 hover:bg-white/90 shadow-lg active:scale-[0.98]"
                        >
                          {e.cta_text}
                        </a>
                      ) : (
                        <Link
                          to={`/${e.cta_section}`}
                          onClick={() => {
                            setCurrentSection(e.cta_section);
                            if (e.cta_tab) setActiveTab(e.cta_tab);
                          }}
                          className="btn bg-white text-brand-800 text-base font-semibold px-7 py-3.5 hover:bg-white/90 shadow-lg active:scale-[0.98]"
                        >
                          {e.cta_text}
                        </Link>
                      )
                    }
                  </div>
                )}
              </div>
            </TextStep>
          ))}
        </div>

      </div>
    </section>
  );
};

export default CurrentEvents;
